import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import "../../styles/adminPanels.css";
import "./questionadmin.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { getDvSourceImageUrl } from "../../../functions/themeAssets";
import QuestionAdminTour from "./QuestionAdminTour";

const TOUR_SEEN_KEY = "dyk_admin_questionadmin_tour_seen_v1";

const MAX_QUESTION_LEN = 250;
const MAX_OPTION_LEN = 80;
const MAX_POINTS = 100;
const MAX_TOTAL_QUESTIONS = 100;
const MAX_PER_CATEGORY = 50;
const PAGE_SIZE = 10;
const OPTION_KEYS = ["option_one", "option_two", "option_three", "option_four"];
const MAX_HINT_FILE_MB = 10;
const HINT_FILE_ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,.mkv,.avi,.mp3,.wav,.ogg,.m4a";
const HINT_VIDEO_EXT = ["mp4", "mov", "webm", "mkv", "avi"];
const HINT_AUDIO_EXT = ["mp3", "wav", "ogg", "m4a"];

const emptyQuestion = (defaultCategory = "") => ({
  id: null,
  category: defaultCategory,
  question_name: "",
  option_one: "",
  option_two: "",
  option_three: "",
  option_four: "",
  correct_answer: "",
  answer_id: "",
  question_hint: "",
});

function getHintMediaKind(filename) {
  const ext = String(filename || "").split(".").pop().toLowerCase();
  if (HINT_VIDEO_EXT.includes(ext)) return "video";
  if (HINT_AUDIO_EXT.includes(ext)) return "audio";
  if (ext) return "image";
  return null;
}

function ensureArray(val) {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function ensureCategoryCountMap(val) {
  if (val && typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      if (p && typeof p === "object" && !Array.isArray(p)) return p;
    } catch {
      // fall through
    }
  }
  return {};
}

function parseStoredBool(raw, fallback) {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

const QuestionAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const categories = ensureArray(data?.categories);
  const categoriesStatus = parseStoredBool(data?.categories_status, true);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState(emptyQuestion());
  const [activeCategoryFilter, setActiveCategoryFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState("");
  const csvInputRef = useRef(null);

  // Question hint media (image/gif/video/audio) uploaded from the add/edit form.
  const [hintUploading, setHintUploading] = useState(false);
  const hintFileInputRef = useRef(null);

  // Subject-wise upload: a CSV scoped to whichever category tab is selected (no
  // category column needed — it replaces only that category's questions).
  const [categoryCsvFile, setCategoryCsvFile] = useState(null);
  const [categoryCsvFileName, setCategoryCsvFileName] = useState("");
  const [categoryCsvUploading, setCategoryCsvUploading] = useState(false);
  const categoryCsvInputRef = useRef(null);

  const [correctPoints, setCorrectPoints] = useState("1");
  const [wrongPoints, setWrongPoints] = useState("0");
  const [shuffleQuestions, setShuffleQuestions] = useState(true);
  const [hintEnabled, setHintEnabled] = useState(true);
  const [totalQuestions, setTotalQuestions] = useState("20");
  const [perCategoryCounts, setPerCategoryCounts] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [tourOpen, setTourOpen] = useState(false);

  const tourSteps = useMemo(() => {
    const steps = [
      {
        target: '[data-tour="qa-add-question"]',
        title: "Add a question",
        content: "Create a new question one at a time — pick a category, fill in the four options, and mark the correct one.",
        placement: "bottom",
      },
      {
        target: '[data-tour="qa-upload-csv"]',
        title: "Bulk import via CSV",
        content: "Already have a question bank? Upload a CSV to replace questions across every category in one go.",
        placement: "bottom",
      },
      {
        target: '[data-tour="qa-export-csv"]',
        title: "Export your questions",
        content: "Download every current question as a CSV — handy for backups or bulk editing offline.",
        placement: "bottom",
      },
      {
        target: '[data-tour="qa-sample-csv"]',
        title: "Not sure of the format?",
        content: "Grab a sample CSV with the exact headers expected by the importer.",
        placement: "bottom",
      },
    ];

    if (categoriesStatus) {
      steps.push({
        target: '[data-tour="qa-category-tabs"]',
        title: "Filter by category",
        content: "Switch tabs to filter the table below, or to upload a CSV scoped to just that one category.",
        placement: "bottom",
      });
    }

    steps.push(
      {
        target: '[data-tour="qa-table"]',
        title: "Your question bank",
        content: "Every saved question lives here. Use the edit and delete icons on each row to manage them.",
        placement: "top",
      },
      {
        target: '[data-tour="qa-settings"]',
        title: "Game settings",
        content: "Set scoring, shuffle, hints, and how many questions each player sees — per category if you use them.",
        placement: "left",
      },
      {
        target: '[data-tour="qa-save-settings"]',
        title: "Don't forget to save",
        content: "Settings changes here only take effect once you hit Save All.",
        placement: "top",
      }
    );

    return steps;
  }, [categoriesStatus]);

  const closeTour = useCallback(() => {
    setTourOpen(false);
    try {
      localStorage.setItem(TOUR_SEEN_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  // Auto-launch the guided tour the first time an admin lands on this page.
  useEffect(() => {
    if (loading) return;
    let seen = true;
    try {
      seen = localStorage.getItem(TOUR_SEEN_KEY) === "1";
    } catch {
      seen = true;
    }
    if (!seen) {
      const t = setTimeout(() => setTourOpen(true), 500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [loading]);

  const loadQuestions = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getCategoryQuestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      const json = await res.json();
      if (json.success) setQuestions(json.questions || []);
      else throw new Error(json.message || "Failed to load questions");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to load questions", "error");
    } finally {
      setLoading(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  useEffect(() => {
    if (currentTheme && adminToken) {
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTheme, adminToken]);

  // Reset to page 1 whenever the visible list changes shape (filter switched, or
  // questions added/edited/deleted/imported) so we never land on a now-empty page.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategoryFilter, questions]);

  useEffect(() => {
    if (!data) return;
    setCorrectPoints(String(clampInt(data.points, 1, MAX_POINTS, 1)));
    setWrongPoints(String(clampInt(data.wrong_points, 0, MAX_POINTS, 0)));
    setShuffleQuestions(parseStoredBool(data.shuffle_questions, true));
    setHintEnabled(parseStoredBool(data.hint, true));
    setTotalQuestions(String(clampInt(data.total_question, 1, MAX_TOTAL_QUESTIONS, 20)));
    setPerCategoryCounts(ensureCategoryCountMap(data.categoryQuestions));
  }, [data]);

  const handleFormChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleOptionChange = (key, idx, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (Number(prev.answer_id) === idx + 1) next.correct_answer = value;
      return next;
    });
  };

  const handlePickCorrectOption = (key, idx) => {
    setFormData((prev) => ({ ...prev, answer_id: String(idx + 1), correct_answer: prev[key] }));
  };

  const handleHintFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    const validExt = HINT_FILE_ACCEPT.split(",").map((s) => s.replace(".", ""));
    if (!validExt.includes(ext)) {
      Swal.fire("Error", `Unsupported file type. Allowed: ${validExt.join(", ")}`, "error");
      if (hintFileInputRef.current) hintFileInputRef.current.value = "";
      return;
    }
    if (file.size > MAX_HINT_FILE_MB * 1024 * 1024) {
      Swal.fire("Error", `File must be ${MAX_HINT_FILE_MB}MB or smaller`, "error");
      if (hintFileInputRef.current) hintFileInputRef.current.value = "";
      return;
    }

    setHintUploading(true);
    try {
      // Step 1: ask the backend for a presigned S3 PUT URL (small JSON call —
      // the file's bytes never pass through the Lambda, which otherwise caps
      // request payloads at 6MB and would 413 on anything but tiny images).
      const presignRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadQuestionHint`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
          fileSize: file.size,
        }),
      });
      const presignJson = await presignRes.json();
      if (!presignJson.success) throw new Error(presignJson.message || "Failed to prepare upload");

      // Step 2: upload the file straight to S3 with that URL.
      const putRes = await fetch(presignJson.presignedUpload.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putRes.ok) throw new Error("Failed to upload file to storage");

      // Not persisted to the question until Save/Update is clicked below.
      setFormData((prev) => ({ ...prev, question_hint: presignJson.file }));
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to upload file", "error");
    } finally {
      setHintUploading(false);
      if (hintFileInputRef.current) hintFileInputRef.current.value = "";
    }
  };

  const handleRemoveHintFile = () => {
    setFormData((prev) => ({ ...prev, question_hint: "" }));
  };

  const validateForm = () => {
    if (categoriesStatus && !formData.category) {
      Swal.fire("Validation", "Please select a category", "warning");
      return false;
    }
    if (!formData.question_name.trim()) {
      Swal.fire("Validation", "Question is required", "warning");
      return false;
    }
    if (formData.question_name.length > MAX_QUESTION_LEN) {
      Swal.fire("Validation", `Question must not exceed ${MAX_QUESTION_LEN} characters`, "warning");
      return false;
    }
    const options = OPTION_KEYS.map((k) => formData[k]);
    if (options.some((o) => !o.trim())) {
      Swal.fire("Validation", "All four options are required", "warning");
      return false;
    }
    if (options.some((o) => o.length > MAX_OPTION_LEN)) {
      Swal.fire("Validation", `Each option must not exceed ${MAX_OPTION_LEN} characters`, "warning");
      return false;
    }
    const answerIdNum = Number(formData.answer_id);
    if (!answerIdNum || answerIdNum < 1 || answerIdNum > 4) {
      Swal.fire("Validation", "Select the correct answer", "warning");
      return false;
    }
    if (options[answerIdNum - 1].trim().toLowerCase() !== formData.correct_answer.trim().toLowerCase()) {
      Swal.fire("Validation", "Correct answer does not match the selected option", "warning");
      return false;
    }
    return true;
  };

  const saveQuestionsToBackend = async (list) => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveCategoryQuestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          questions: list.map((q, i) => ({
            category: q.category,
            question_name: q.question_name,
            option_one: q.option_one,
            option_two: q.option_two,
            option_three: q.option_three,
            option_four: q.option_four,
            correct_answer: q.correct_answer,
            answer_id: q.answer_id,
            question_hint: q.question_hint || "",
            sort_order: i + 1,
          })),
        }),
      });
      const json = await res.json();
      if (json.success) {
        Swal.fire("Success", "Questions saved successfully", "success");
        setShowAddForm(false);
        setEditingQuestion(null);
        setFormData(emptyQuestion());
        await loadQuestions();
      } else {
        throw new Error(json.message || "Failed to save questions");
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save questions", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!validateForm()) return;
    const updated = editingQuestion
      ? questions.map((q) => (q.id === editingQuestion.id ? { ...formData, id: q.id } : q))
      : [...questions, { ...formData, id: `new-${Date.now()}` }];
    await saveQuestionsToBackend(updated);
  };

  const handleDeleteQuestion = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This question will be deleted permanently",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      const updated = questions.filter((q) => q.id !== id);
      await saveQuestionsToBackend(updated);
    }
  };

  const handleEditQuestion = (q) => {
    setEditingQuestion(q);
    setFormData({ ...q, answer_id: String(q.answer_id) });
    setShowAddForm(true);
  };

  const resetCsvSelection = () => {
    setCsvFile(null);
    setCsvFileName("");
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const handleCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "csv") {
      Swal.fire("Error", "Only CSV files allowed", "error").then(resetCsvSelection);
      return;
    }
    setCsvFile(file);
    setCsvFileName(file.name);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      Swal.fire("Info", "Please select a CSV file first", "info");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("organizationId", organizationId || "admin");
      fd.append("sessionId", sessionId || "admin");
      fd.append("currentTheme", currentTheme);
      fd.append("csvFile", csvFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/importCategoryQuestionsCSV`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (json.success) {
        Swal.fire({
          icon: "success",
          title: "Import Successful!",
          html: `<p>${json.message || "CSV imported successfully"}</p><p>Total questions imported: <strong>${json.count || 0}</strong></p>`,
        });
        setShowCsvModal(false);
        resetCsvSelection();
        await loadQuestions();
      } else {
        throw new Error(json.message || "Failed to import CSV");
      }
    } catch (e) {
      Swal.fire("Please check CSV", e.message || "Failed to import CSV", "error").then(resetCsvSelection);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (questions.length === 0) {
      Swal.fire("Info", "No questions to export", "info");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/exportCategoryQuestionsCSV`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      if (!res.ok) throw new Error("Failed to export CSV");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "category_questions.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to export CSV", "error");
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleCsv = () => {
    const headers = ["category", "question_name", "option_one", "option_two", "option_three", "option_four", "correct_answer", "answer_id"];
    const sample = [categories[0] || "Category", "Sample question text?", "Option A", "Option B", "Option C", "Option D", "Option A", "1"];
    const csvContent = [headers.join(","), sample.map((c) => `"${c}"`).join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "category_questions_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetCategoryCsvSelection = () => {
    setCategoryCsvFile(null);
    setCategoryCsvFileName("");
    if (categoryCsvInputRef.current) categoryCsvInputRef.current.value = "";
  };

  const handleCategoryCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "csv") {
      Swal.fire("Error", "Only CSV files allowed", "error").then(resetCategoryCsvSelection);
      return;
    }
    setCategoryCsvFile(file);
    setCategoryCsvFileName(file.name);
  };

  const handleCategoryCsvUpload = async () => {
    if (!categoryCsvFile) {
      Swal.fire("Info", "Please select a CSV file first", "info");
      return;
    }
    setCategoryCsvUploading(true);
    try {
      const fd = new FormData();
      fd.append("organizationId", organizationId || "admin");
      fd.append("sessionId", sessionId || "admin");
      fd.append("currentTheme", currentTheme);
      fd.append("category", activeCategoryFilter);
      fd.append("csvFile", categoryCsvFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/importCategoryQuestionsCSV`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (json.success) {
        Swal.fire({
          icon: "success",
          title: "Import Successful!",
          html: `<p>${json.message || "CSV imported successfully"}</p><p>Total questions imported for ${activeCategoryFilter}: <strong>${json.count || 0}</strong></p>`,
        });
        resetCategoryCsvSelection();
        await loadQuestions();
      } else {
        throw new Error(json.message || "Failed to import CSV");
      }
    } catch (e) {
      Swal.fire("Please check CSV", e.message || "Failed to import CSV", "error").then(resetCategoryCsvSelection);
    } finally {
      setCategoryCsvUploading(false);
    }
  };

  const downloadCategorySampleCsv = () => {
    const headers = ["question_name", "option_one", "option_two", "option_three", "option_four", "correct_answer", "answer_id"];
    const sample = ["Sample question text?", "Option A", "Option B", "Option C", "Option D", "Option A", "1"];
    const csvContent = [headers.join(","), sample.map((c) => `"${c}"`).join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeCategoryFilter || "category"}_sample.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const validateSettings = (totalOverride = totalQuestions, countsOverride = perCategoryCounts) => {
    const totalClamped = clampInt(totalOverride, 1, MAX_TOTAL_QUESTIONS, 20);
    const normalizedCounts = {};
    let sum = 0;

    if (categoriesStatus) {
      for (const cat of categories) {
        const count = clampInt(countsOverride[cat], 0, MAX_PER_CATEGORY, 0);
        const available = groupedByCategory[cat]?.length || 0;
        if (count > available) {
          return {
            ok: false,
            msg: `${cat} only has ${available} question${available === 1 ? "" : "s"} in the database, but ${count} ${count === 1 ? "was" : "were"} requested.`,
          };
        }
        normalizedCounts[cat] = count;
        sum += count;
      }

      if (categories.length > 0 && sum !== totalClamped) {
        return {
          ok: false,
          msg: `The category counts add up to ${sum}, but "No of questions to display" is set to ${totalClamped}. Please make these match.`,
        };
      }
    }

    return { ok: true, totalClamped, normalizedCounts };
  };

  const handleSaveSettings = async () => {
    const v = validateSettings();
    if (!v.ok) {
      Swal.fire("Validation", v.msg, "warning");
      return;
    }
    setSettingsSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            points: clampInt(correctPoints, 1, MAX_POINTS, 1),
            wrong_points: clampInt(wrongPoints, 0, MAX_POINTS, 0),
            shuffle_questions: shuffleQuestions,
            hint: hintEnabled,
            total_question: v.totalClamped,
            categoryQuestions: v.normalizedCounts,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Settings saved", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save settings", "error");
    } finally {
      setSettingsSaving(false);
    }
  };

  const groupedByCategory = categories.reduce((acc, cat) => {
    acc[cat] = questions.filter((q) => q.category === cat);
    return acc;
  }, {});

  const filteredQuestions =
    activeCategoryFilter === "All" ? questions : questions.filter((q) => q.category === activeCategoryFilter);

  const totalPages = Math.max(1, Math.ceil(filteredQuestions.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedQuestions = filteredQuestions.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder" style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button type="button" className="qa-tour-launch" onClick={() => setTourOpen(true)}>
          <i className="fa-solid fa-circle-question" /> Take a Tour
        </button>
      </div>

      <div className="questionadmin-layout">
        <div className="questionadmin-main">
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Question Administration</div>

            <div className="themeupdate-actions-row">
              <button
                type="button"
                data-tour="qa-add-question"
                className="themeupdate-btn themeupdate-btn-success"
                onClick={() => {
                  setShowAddForm(true);
                  setEditingQuestion(null);
                  setFormData(emptyQuestion(categoriesStatus ? categories[0] || "" : categories[0] || "General"));
                }}
                disabled={loading}
              >
                <i className="fa-solid fa-plus" /> Add Question
              </button>
              <button
                type="button"
                data-tour="qa-upload-csv"
                className="themeupdate-btn themeupdate-btn-primary"
                onClick={() => setShowCsvModal(true)}
                disabled={loading}
              >
                <i className="fa-solid fa-upload" /> Upload CSV
              </button>
              <button
                type="button"
                data-tour="qa-export-csv"
                className="themeupdate-btn themeupdate-btn-primary"
                onClick={handleExportCsv}
                disabled={loading || questions.length === 0}
              >
                <i className="fa-solid fa-download" /> Export CSV
              </button>
              <button type="button" data-tour="qa-sample-csv" className="themeupdate-btn themeupdate-btn-outline" onClick={downloadSampleCsv}>
                <i className="fa-solid fa-file-csv" /> Sample CSV
              </button>
            </div>

            {categoriesStatus && (
              <>
                <div className="themeupdate-tabs questionadmin-filter-tabs" data-tour="qa-category-tabs">
                  <button
                    type="button"
                    className={`themeupdate-tab ${activeCategoryFilter === "All" ? "active" : ""}`}
                    onClick={() => {
                      setActiveCategoryFilter("All");
                      resetCategoryCsvSelection();
                    }}
                  >
                    All
                  </button>
                  {categories.map((cat) => (
                    <button
                      type="button"
                      key={cat}
                      className={`themeupdate-tab ${activeCategoryFilter === cat ? "active" : ""}`}
                      onClick={() => {
                        setActiveCategoryFilter(cat);
                        resetCategoryCsvSelection();
                      }}
                    >
                      {cat} ({groupedByCategory[cat]?.length || 0})
                    </button>
                  ))}
                </div>

                {activeCategoryFilter === "All" ? (
                  <p className="questionadmin-category-filter-hint">
                    <i className="fa-solid fa-circle-info" /> <strong>All</strong> is a filter only. Select a subject above to upload a
                    CSV for that category.
                  </p>
                ) : (
                  <div className="questionadmin-category-upload">
                    <h5 className="questionadmin-category-upload-title">Upload CSV for {activeCategoryFilter}</h5>
                    <div className="questionadmin-category-upload-row">
                      <input
                        ref={categoryCsvInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleCategoryCsvFileSelect}
                        className="themeupdate-file-input"
                        id="category-scoped-csv-input"
                      />
                      <label htmlFor="category-scoped-csv-input" className="themeupdate-file-label-large questionadmin-category-upload-label">
                        <i className="fa-solid fa-file-csv" />
                        <span>{categoryCsvFileName || "Choose CSV File"}</span>
                      </label>
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-primary"
                        onClick={handleCategoryCsvUpload}
                        disabled={!categoryCsvFile || categoryCsvUploading}
                      >
                        {categoryCsvUploading ? "Uploading..." : "Upload"}
                      </button>
                      <button type="button" className="themeupdate-btn themeupdate-btn-outline" onClick={downloadCategorySampleCsv}>
                        <i className="fa-solid fa-file-csv" /> Sample CSV
                      </button>
                    </div>
                    <p className="themeupdate-muted">
                      <code>
                        * CSV uploads must be done subject-wise.
                        <br />* Uploading replaces all existing questions in this category.
                        <br />* Do not include a category column — headers: question_name, option_one, option_two, option_three,
                        option_four, correct_answer, answer_id
                      </code>
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="themeupdate-table-section" data-tour="qa-table">
              {loading ? (
                <div className="themeupdate-loading">Loading questions...</div>
              ) : filteredQuestions.length === 0 ? (
                <div className="themeupdate-empty">
                  <i className="fa-solid fa-inbox" />
                  <p>No questions yet. Add questions using the form above or import from CSV.</p>
                </div>
              ) : (
                <div className="themeupdate-table-wrapper">
                  <table className="themeupdate-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {categoriesStatus && <th>Category</th>}
                        <th>Question</th>
                        <th>Correct Answer</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedQuestions.map((q, i) => (
                        <tr key={q.id}>
                          <td>{pageStart + i + 1}</td>
                          {categoriesStatus && (
                            <td>
                              <span className="themeupdate-badge-pill">{q.category}</span>
                            </td>
                          )}
                          <td className="themeupdate-td-ellipsis">{q.question_name}</td>
                          <td className="themeupdate-td-ellipsis">{q.correct_answer}</td>
                          <td>
                            <div className="themeupdate-table-actions">
                              <button
                                type="button"
                                className="themeupdate-btn-icon themeupdate-btn-icon-edit"
                                onClick={() => handleEditQuestion(q)}
                                title="Edit"
                              >
                                <i className="fa-solid fa-edit" />
                              </button>
                              <button
                                type="button"
                                className="themeupdate-btn-icon themeupdate-btn-icon-delete"
                                onClick={() => handleDeleteQuestion(q.id)}
                                title="Delete"
                              >
                                <i className="fa-solid fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {filteredQuestions.length > 0 && (
                <div className="questionadmin-pagination">
                  <span className="questionadmin-pagination-info">
                    Showing {pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, filteredQuestions.length)} of{" "}
                    {filteredQuestions.length} entries
                  </span>
                  <div className="questionadmin-pagination-controls">
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-outline"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pageNum) => (
                      <button
                        type="button"
                        key={pageNum}
                        className={`questionadmin-pagination-page ${pageNum === safePage ? "active" : ""}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-outline"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="questionadmin-sidebar">
          <div className="themeupdate-card" data-tour="qa-settings">
            <div className="themeupdate-card-header">Settings</div>
            <div className="themeupdate-form-section">
              <div className="themeupdate-form-group">
                <label className="themeupdate-label">Correct Answer Points</label>
                <input
                  type="number"
                  min="1"
                  max={MAX_POINTS}
                  className="themeupdate-input"
                  value={correctPoints}
                  onChange={(e) => setCorrectPoints(e.target.value)}
                  onBlur={() => setCorrectPoints(String(clampInt(correctPoints, 1, MAX_POINTS, 1)))}
                />
                <div className="themeupdate-hint">* Use arrows to set points (1-{MAX_POINTS}) for each correct answer.</div>
              </div>

              <div className="themeupdate-form-group">
                <label className="themeupdate-label">Incorrect Answer Points</label>
                <input
                  type="number"
                  min="0"
                  max={MAX_POINTS}
                  className="themeupdate-input"
                  value={wrongPoints}
                  onChange={(e) => setWrongPoints(e.target.value)}
                  onBlur={() => setWrongPoints(String(clampInt(wrongPoints, 0, MAX_POINTS, 0)))}
                />
                <div className="themeupdate-hint">
                  * Use arrows to set penalty points (0-{MAX_POINTS}, default 0). This amount is deducted for each incorrect answer.
                </div>
              </div>

              <div className="questionadmin-toggle-row">
                <Switch checked={shuffleQuestions} onChange={setShuffleQuestions} onColor="#4caf50" height={22} width={44} />
                <label className="themeupdate-label" style={{ margin: 0 }}>
                  Shuffle Questions
                </label>
              </div>

              <div className="questionadmin-toggle-row">
                <Switch checked={hintEnabled} onChange={setHintEnabled} onColor="#4caf50" height={22} width={44} />
                <label className="themeupdate-label" style={{ margin: 0 }}>
                  Hint
                </label>
              </div>

              <div className="themeupdate-form-group">
                <label className="themeupdate-label">No of questions to display</label>
                <input
                  type="number"
                  min="1"
                  max={MAX_TOTAL_QUESTIONS}
                  className="themeupdate-input"
                  value={totalQuestions}
                  onChange={(e) => setTotalQuestions(e.target.value)}
                  onBlur={() => setTotalQuestions(String(clampInt(totalQuestions, 1, MAX_TOTAL_QUESTIONS, 20)))}
                />
              </div>

              {categoriesStatus && categories.length > 0 && (
                <div className="themeupdate-form-group">
                  <label className="themeupdate-label">No of questions per category</label>
                  {categories.map((cat) => (
                    <div key={cat} className="questionadmin-per-category-row">
                      <span className="questionadmin-per-category-label">
                        {cat} <span className="themeupdate-hint">({groupedByCategory[cat]?.length || 0} in database)</span>
                      </span>
                      <input
                        type="number"
                        min="0"
                        max={MAX_PER_CATEGORY}
                        className="themeupdate-input"
                        value={perCategoryCounts[cat] ?? 0}
                        onChange={(e) => setPerCategoryCounts((prev) => ({ ...prev, [cat]: e.target.value }))}
                        onBlur={() =>
                          setPerCategoryCounts((prev) => ({
                            ...prev,
                            [cat]: clampInt(prev[cat], 0, MAX_PER_CATEGORY, 0),
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="themeupdate-form-actions" style={{ position: "static" }}>
                <button
                  type="button"
                  data-tour="qa-save-settings"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveSettings}
                  disabled={settingsSaving}
                >
                  {settingsSaving ? "Saving..." : "Save All"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCsvModal && (
        <div className="themeupdate-modal-overlay">
          <div className="themeupdate-modal themeupdate-modal-csv" onClick={(e) => e.stopPropagation()}>
            <div className="themeupdate-modal-header">
              <h3 className="themeupdate-modal-title">Upload CSV File</h3>
              <button
                type="button"
                className="themeupdate-modal-close"
                onClick={() => {
                  setShowCsvModal(false);
                  resetCsvSelection();
                }}
                disabled={loading}
              >
                <i className="fa-solid fa-times" />
              </button>
            </div>

            <div className="themeupdate-modal-body">
              <div className="themeupdate-csv-upload-container">
                <div className="themeupdate-csv-warning">
                  <i className="fa-solid fa-exclamation-triangle" />
                  <div>
                    <strong>Warning:</strong> This replaces questions across every category for this theme, not just one — use
                    the category tabs above for a subject-wise upload that only replaces one category.
                    <br />
                    Current questions: <strong>{questions.length}</strong>
                  </div>
                </div>

                <div className="themeupdate-csv-upload-area">
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileSelect}
                    className="themeupdate-file-input"
                    id="category-csv-upload-input"
                  />
                  <label htmlFor="category-csv-upload-input" className="themeupdate-file-label-large">
                    <i className="fa-solid fa-file-csv" />
                    <span>{csvFileName || "Choose CSV File"}</span>
                  </label>
                </div>

                <div className="themeupdate-csv-instructions">
                  <h4>CSV File Requirements:</h4>
                  <ul>
                    <li>
                      Headers:{" "}
                      <code>category, question_name, option_one, option_two, option_three, option_four, correct_answer, answer_id</code>
                    </li>
                    <li>category must match one of this theme's configured categories</li>
                    <li>All four options are required; answer_id must be 1-4 and must match correct_answer</li>
                    <li>
                      Character limits: question ({MAX_QUESTION_LEN}), options ({MAX_OPTION_LEN} each)
                    </li>
                  </ul>
                </div>
              </div>

              <div className="themeupdate-form-actions">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary"
                  onClick={handleCsvUpload}
                  disabled={!csvFile || loading}
                >
                  {loading ? "Uploading..." : "Upload & Replace All Questions"}
                </button>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-outline"
                  onClick={() => {
                    setShowCsvModal(false);
                    resetCsvSelection();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="themeupdate-modal-overlay">
          <div className="themeupdate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="themeupdate-modal-header">
              <h3 className="themeupdate-modal-title">{editingQuestion ? "Edit Question" : "Add New Question"}</h3>
              <button
                type="button"
                className="themeupdate-modal-close"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingQuestion(null);
                  setFormData(emptyQuestion());
                }}
                disabled={loading}
              >
                <i className="fa-solid fa-times" />
              </button>
            </div>

            <div className="themeupdate-modal-body">
              <div className="themeupdate-form-grid">
                {categoriesStatus && (
                  <div className="themeupdate-form-group">
                    <label className="themeupdate-label">Category *</label>
                    <select
                      className="themeupdate-select"
                      value={formData.category}
                      onChange={(e) => handleFormChange("category", e.target.value)}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="themeupdate-form-group themeupdate-form-group-full">
                  <label className="themeupdate-label">Question * (max {MAX_QUESTION_LEN} chars)</label>
                  <textarea
                    className="themeupdate-input themeupdate-textarea"
                    value={formData.question_name}
                    onChange={(e) => handleFormChange("question_name", e.target.value)}
                    maxLength={MAX_QUESTION_LEN}
                    rows={3}
                    placeholder="Enter the question"
                  />
                </div>

                {OPTION_KEYS.map((key, idx) => {
                  const isCorrect = Number(formData.answer_id) === idx + 1;
                  return (
                    <div className="themeupdate-form-group" key={key}>
                      <label className="themeupdate-label">
                        <input
                          type="radio"
                          name="correct-option"
                          checked={isCorrect}
                          onChange={() => handlePickCorrectOption(key, idx)}
                          style={{ marginRight: 6 }}
                        />
                        Option {idx + 1} * (max {MAX_OPTION_LEN} chars)
                        {isCorrect && <span className="questionadmin-correct-badge">Correct</span>}
                      </label>
                      <input
                        type="text"
                        className="themeupdate-input"
                        value={formData[key]}
                        maxLength={MAX_OPTION_LEN}
                        placeholder={`Option ${idx + 1}`}
                        onChange={(e) => handleOptionChange(key, idx, e.target.value)}
                      />
                    </div>
                  );
                })}

                <div className="themeupdate-form-group themeupdate-form-group-full">
                  <label className="themeupdate-label">
                    Question Hint (optional — image, gif, video or audio, max {MAX_HINT_FILE_MB}MB)
                  </label>
                  {formData.question_hint ? (
                    <div className="questionadmin-hint-preview">
                      {getHintMediaKind(formData.question_hint) === "video" ? (
                        <video
                          className="questionadmin-hint-preview-media"
                          src={getDvSourceImageUrl(formData.question_hint)}
                          controls
                        />
                      ) : getHintMediaKind(formData.question_hint) === "audio" ? (
                        <audio
                          className="questionadmin-hint-preview-media"
                          src={getDvSourceImageUrl(formData.question_hint)}
                          controls
                        />
                      ) : (
                        <img
                          className="questionadmin-hint-preview-media"
                          src={getDvSourceImageUrl(formData.question_hint)}
                          alt="Question hint"
                        />
                      )}
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-danger"
                        onClick={handleRemoveHintFile}
                        disabled={hintUploading}
                      >
                        <i className="fa-solid fa-times" /> Remove
                      </button>
                    </div>
                  ) : (
                    <div className="questionadmin-hint-upload-row">
                      <input
                        ref={hintFileInputRef}
                        type="file"
                        accept={HINT_FILE_ACCEPT}
                        onChange={handleHintFileSelect}
                        className="themeupdate-file-input"
                        id="question-hint-file-input"
                        disabled={hintUploading}
                      />
                      <label htmlFor="question-hint-file-input" className="themeupdate-file-label-large">
                        <i className="fa-solid fa-file-upload" />
                        <span>{hintUploading ? "Uploading..." : "Choose File"}</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="themeupdate-form-actions">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveQuestion}
                  disabled={loading}
                >
                  {loading ? "Saving..." : editingQuestion ? "Update Question" : "Add Question"}
                </button>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-outline"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingQuestion(null);
                    setFormData(emptyQuestion());
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <QuestionAdminTour steps={tourSteps} active={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default QuestionAdmin;
